/**
 * First-party guest answers. No xAI, no Mac, no other law firm.
 * Always returns text for a non-empty query.
 */
export const KERNEL_ENGINE = 'extensus-kernel';
export const KERNEL_MODEL = 'utah-76-18';

export function isStubEdgeText(text) {
  const t = String(text || '').trim();
  if (!t) return true;
  return (
    /This device is the node/i.test(t)
    || /Keep the tab open/i.test(t)
    || /does not run on the operator Mac/i.test(t)
    || /^native heartbeat$/i.test(t)
    || /cannot change Tony Mac/i.test(t)
    || /guest_system_write_blocked/i.test(t)
    || /Writes stay in \/Users\/tonyc/i.test(t)
    || /global Teleporte\. Talk is open/i.test(t)
  );
}

const BLOCKED_RE =
  /264100175|261100245|254100338|254100371|tayler|frazier|c001783920|ready-to-sign|sapcr|greenfiling|mypaperwork|ocap\/|playbook\/2[01]_|playbook\/7[01278]_|playbook\/84_|texas|extosecure|extoheal/i;

function blobOf(query, history = []) {
  const prior = (history || []).map((m) => String(m?.content || '')).join('\n');
  return `${prior}\n${query || ''}`.trim();
}

function fact(blob, re, fallback = '') {
  const m = String(blob || '').match(re);
  return m ? String(m[1] || m[0]).trim() : fallback;
}

function isPleaWithdraw(t) {
  return /withdraw|take back|undo|vacate/i.test(t)
    && /plea|admission|guilty|no.?contest|blanket/i.test(t);
}

function isSentencing(t) {
  return /sentenc/i.test(t) || /court (is )?soon/i.test(t) || /next court/i.test(t);
}

function isTellLawyer(t) {
  return /tell my lawyer|get him to file|take me serious|public defender|\bpd\b|appointed counsel|my attorney/i.test(t);
}

function isDistribution(t) {
  return /distribut|intent to (sell|distribut)|dealer|scale|baggies|13 carts|carts/i.test(t);
}

function isPossession(t) {
  return /possess|personal use|i only (owned|had)/i.test(t);
}

function isDiscovery(t) {
  return /bodycam|dashcam|lab|nik|chain of custody|discovery|warrant/i.test(t);
}

function isDocuments(t) {
  return /how do i (send|put|give|upload|attach)|send documents|attach (a |the )?(file|pdf|photo|paper)|upload/i.test(t)
    && !/Attached files:/i.test(t)
    && !/<packet_brief>/i.test(t);
}

function isPacket(t) {
  return /Attached files:/i.test(t)
    || /\/inbox\/\S+\.pdf/i.test(t)
    || /<packet_brief>/i.test(t)
    || /review (my |the )?(files|packet|docs|documents|pdfs)/i.test(t);
}

function kindFromName(name) {
  const n = String(name || '').toLowerCase();
  if (/petition/.test(n)) return 'petition';
  if (/returnofservice|return-of-service/.test(n)) return 'return of service';
  if (/affidavit/.test(n)) return 'warrant affidavit';
  if (/warrant/.test(n)) return 'search warrant';
  if (/mushroom|lab|nik|testresult/.test(n)) return 'field / lab slip';
  if (/officer|report|incident/.test(n)) return 'officer report (scan)';
  return 'document';
}

function section(blob, heading) {
  const re = new RegExp(`${heading}\\n([\\s\\S]*?)(?=\\n[A-Z][A-Z ]+\\n|<\\/packet_brief>|$)`);
  const m = String(blob || '').match(re);
  return m ? m[1].trim() : '';
}

function packetAnswer(blob) {
  const brief = String(blob || '').match(/<packet_brief>([\s\S]*?)<\/packet_brief>/);
  if (brief) {
    const body = brief[1];
    const caseLine = fact(body, /^case=(.+)$/m);
    const incident = fact(body, /^incident=(.+)$/m);
    const charges = section(body, 'CHARGES');
    const inventory = section(body, 'INVENTORY');
    const lab = section(body, 'LAB');
    const gaps = section(body, 'GAPS');
    const attacks = section(body, 'ATTACKS');
    return [
      caseLine ? `I read the packet. ${caseLine}.` : 'I read the packet.',
      incident ? `Incident: ${incident}.` : '',
      charges ? `Charges on the petition:\n${charges}` : '',
      inventory ? `What the affidavits say was in the car:\n${inventory}` : '',
      lab ? `Lab in this drop:\n${lab}` : '',
      attacks ? `How I fight it:\n${attacks}` : [
        '76-18-209 distribution needs a distribute OR agree/consent/offer/arrange. Quantity in a car is not that element by itself.',
        '76-18-207 possession is knowledge plus dominion/control. Passenger presence is not the inventory.',
        'A field mushroom test is not a state crime lab. Demand lab ID, chain, bodycam, dashcam — Rule 16.',
      ].join('\n'),
      gaps ? `Still missing:\n${gaps}` : '',
      'I will not Sign or e-file. Hand this to your lawyer. If a plea is already on the record and sentencing is coming, Rule 11 withdrawal is the move — say the date and I will draft the ask.',
    ].filter(Boolean).join('\n\n');
  }
  const names = [...blob.matchAll(/\/(?:\d{10,}-)?([^\/\s]+\.pdf)/gi)].map((m) => m[1]);
  const unique = [...new Set(names.map((n) => n.replace(/^\d+-/, '')))];
  const labeled = unique.map((n) => `${kindFromName(n)}: ${n}`);
  const hasPetition = unique.some((n) => /petition/i.test(n));
  const hasScan = unique.some((n) => /officer|report/i.test(n));
  const hasLab = unique.some((n) => /mushroom|lab|testresult/i.test(n));
  const hasWarrant = unique.some((n) => /warrant/i.test(n));
  return [
    unique.length
      ? `Got ${unique.length} file(s):\n${labeled.map((l) => `- ${l}`).join('\n')}`
      : 'Got the packet.',
    hasPetition
      ? 'The petition is the charging paper. I read extractable text in parallel and map counts onto 76-18-207 possession vs 76-18-209 distribution.'
      : 'If there is a petition in here, that is the charging paper — not a police referral.',
    hasWarrant
      ? 'Warrant / affidavit / return-of-service sets get read for probable cause, who was driver vs passenger, and what they claim was in the car vs on a person.'
      : '',
    hasLab
      ? 'A mushroom / field-test slip is not a state crime lab. I will say so in the review.'
      : '',
    hasScan
      ? 'The officer report looks like a scan. Short OCR on the Mac (first pages + last), not a 15-minute grind of every page.'
      : 'Scans get a short OCR pass, not a 15-minute grind.',
    'Utah frame while it lands: passenger presence is not the inventory. 76-18-209 needs distribute or agree/offer/arrange. Never Sign. Never e-file.',
    'Tell me the ask: attack the distribution count, draft the Rule 11 letter, or list what is still missing.',
  ].filter(Boolean).join('\n');
}

function pleaLetter({ date, carts, cash, confused, charge }) {
  const when = date || 'the next hearing';
  const qty = carts || 'the amount I actually had';
  const money = cash || 'where the cash came from';
  const mix = confused ? 'I was confused when I took that plea. I did not understand I was admitting distribution.' : 'I did not understand the elements I was admitting.';
  return [
    `I need you to file a Motion to Withdraw my admission / guilty plea before ${when}. That date is sentencing. If we wait, this gets much harder.`,
    '',
    'Legal frame: Utah R. Crim. P. 11. Before sentencing the court can allow withdrawal for good cause / a fair and just reason. After sentencing it is basically manifest injustice. File now.',
    '',
    'Facts to put in the motion and in my affidavit:',
    `- Charge as pled: ${charge}.`,
    `- What I actually had: ${qty}, for personal use.`,
    `- Money: ${money}.`,
    `- ${mix}`,
    '- I did not agree, offer, or arrange to distribute. Presence plus a count is not 76-18-209 by itself.',
    '- Ask the court to restore me to a 76-18-207 possession fight, or at least to a not-guilty / not-admitted posture so we can litigate elements.',
    '',
    'Also demand: lab ID (NIK is not a crime lab), chain of custody, bodycam, dashcam, the charging information, and the plea colloquy transcript.',
    '',
    'Email this today. Hand him a printed copy at the next appearance. If he will not file, put that on the record and ask the judge for new counsel or to let me be heard on withdrawal myself. EXTENSUS will not Sign or e-file this for you.',
  ].join('\n');
}

function pleaAnswer(blob) {
  const date = fact(blob, /(?:sept(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june|july|aug(?:ust)?)\s+\d{1,2}/i)
    || fact(blob, /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/);
  const carts = fact(blob, /(\d+\s+carts?)/i);
  const cash = /grandparent/i.test(blob)
    ? 'cash came from my grandparents, not from sales'
    : fact(blob, /cash came from ([^.]+)/i);
  const confused = /confused/i.test(blob);
  const charge = /distribut/i.test(blob)
    ? 'blanket distribution under 76-18-209'
    : 'the admission already on the record';
  const letter = pleaLetter({ date, carts, cash, confused, charge });
  return [
    'Yes. Before sentencing you still have a real shot. After they sentence you, withdrawal is a different and much worse standard.',
    '',
    'Utah R. Crim. P. 11: pre-sentence withdrawal is good cause / fair and just reason. Your facts — personal-use amount, source of the cash, confusion on a blanket distribution plea — are the affidavit. 76-18-209 is distribute OR agree/consent/offer/arrange. 76-18-207 is possession. Those are not the same crime. A blanket “distribution” admission can be attacked if you did not understand the elements and the facts only support possession.',
    '',
    'Your lawyer is counsel of record. He files. You do not Sign and you do not e-file from this chat. You make him take it seriously by putting a written ask in his hands that he would be stupid to ignore at a sentencing setting.',
    '',
    'Send him this, email plus print:',
    '',
    letter,
  ].join('\n');
}

function possessionAnswer() {
  return [
    '76-18-207 possession: knowingly and intentionally possesses or uses. Marijuana possession is generally class B unless the weight is huge (76-18-207(3)(d)).',
    'Constructive possession in a car is knowledge plus dominion/control. Passenger presence is not enough.',
    'If the State is charging distribution (76-18-209) off quantity, packaging, cash, or texts, make them prove an agree/offer/arrange or an actual distribute. No sale has to be watched, but they still need that element.',
    'Demand lab ID. NIK field tests are not the crime lab. Never Sign. Never e-file.',
  ].join('\n');
}

function distributionAnswer() {
  return [
    '76-18-209 distribution: knowingly and intentionally distributes OR agrees, consents, offers, or arranges to distribute. First-time marijuana / Schedule III-IV is a third-degree felony under 76-18-209(3)(a)(ii).',
    'Intent can be inferred from quantity, packaging, cash, texts. It cannot be inferred from you sitting in a car. Passenger presence is not the inventory.',
    'If you only had a personal-use amount and the cash has a non-sale source, that is the possession fight (76-18-207), not a clean distribution plea.',
    'If a plea already happened, do not sit on it through sentencing. Withdrawal is Rule 11. Never Sign. Never e-file.',
  ].join('\n');
}

function discoveryAnswer() {
  return [
    'Utah R. Crim. P. 16: bodycam, dashcam, warrants, photos, lab, chain of custody. Ask in writing.',
    'NIK field tests are not a state crime lab. Demand a lab identification of the substance.',
    'A police referral is not a petition or an information. Confirm the actual charging paper before you talk plea.',
    'Never Sign. Never e-file.',
  ].join('\n');
}

function documentsAnswer() {
  return [
    'Tap the + next to the box. Pick the PDF, photo, or scan. It stays in YOUR packet only.',
    'Then tell me what you want from it: what charge, what date, what you need drafted.',
    'I will not Sign or e-file. I will tell you exactly what to hand your lawyer or the clerk.',
  ].join('\n');
}

function genericAnswer(query) {
  return [
    `You asked: ${String(query).slice(0, 280)}`,
    '',
    'Utah Title 76 Chapter 18 (May 6, 2026): 76-18-207 possession, 76-18-209 distribution. Passenger presence is not enough. NIK is not a lab. Plea withdrawal before sentencing is Utah R. Crim. P. 11 (good cause / fair and just reason). After sentencing it is much harder.',
    '',
    'Tell me the charging paper (possession vs distribution), whether a plea already went on the record, and the next hearing date. I will draft the exact words. I will not Sign or e-file. You use this at your own risk. If you want a lawyer in this chat, that is me.',
  ].join('\n');
}

export function guestQueryBlocked(query) {
  return BLOCKED_RE.test(String(query || ''));
}

export function guestKernelReply(query, history = []) {
  const q = String(query || '').trim();
  if (!q) return { ok: false, text: '', error: 'empty_query', engine: KERNEL_ENGINE };
  if (guestQueryBlocked(q)) {
    return {
      ok: true,
      text: 'That look-up is outside this guest library. Utah criminal and your own packet only. Ask about your charge, plea, or hearing.',
      engine: KERNEL_ENGINE,
      model: KERNEL_MODEL,
    };
  }
  const blob = blobOf(q, history);
  let text;
  if (isPleaWithdraw(blob) || (isTellLawyer(blob) && (isSentencing(blob) || isDistribution(blob)))) {
    text = pleaAnswer(blob);
    if (isPacket(blob)) text = `${text}\n\n${packetAnswer(blob)}`;
  } else if (isPacket(blob)) {
    text = packetAnswer(blob);
  } else if (isDocuments(blob)) {
    text = documentsAnswer();
  } else if (isDiscovery(blob)) {
    text = discoveryAnswer();
  } else if (isDistribution(blob) && isPossession(blob)) {
    text = [possessionAnswer(), '', distributionAnswer()].join('\n');
  } else if (isDistribution(blob)) {
    text = distributionAnswer();
  } else if (isPossession(blob)) {
    text = possessionAnswer();
  } else {
    text = genericAnswer(q);
  }
  return { ok: true, text, engine: KERNEL_ENGINE, model: KERNEL_MODEL };
}
